import { Button, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useMemo } from "react";
import {
  AppointmentTypeType,
  SLA_HELPER_TEXT,
  addAppointmentTypeInitialValues,
  appointmentTypeValidationSchema,
} from "../../../../constants/ProviderGroupFormInitialValues";
import { DisclosureHost } from "../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { usePatientFormOptions } from "../../../../hooks/use-patient-form-options";
import { useAppointmentTypeCreate } from "../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-create";
import { useAppointmentTypeUpdate } from "../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-update";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../utils/utils";
import CustomSelect from "../../../CustomSelect";
import Input from "../../../Input";
import MaskMultiSelect from "../../../MaskMultiSelect";
import NumberInput from "../../../NumberInput";
import SideSheetDrawer from "../../../SidesheetDrawer";
import Text from "../../../Text";

interface AppointmentTypeModalProps {
  modalTitle: string;
  disclosureHost: DisclosureHost;
}

const AppointmentTypeDrawer = ({
  modalTitle: dialogTitle,
  disclosureHost,
}: AppointmentTypeModalProps) => {
  const {
    values,
    setFieldValue,
    handleBlur,
    handleChange,
    resetForm,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues: disclosureHost?.data || addAppointmentTypeInitialValues,
    validationSchema: appointmentTypeValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const { mutateAsync: addAppointmentType } = useAppointmentTypeCreate();
  const { mutateAsync: updateAppointmentType } = useAppointmentTypeUpdate();

  const client = useQueryClient();
  const showSnackbar = useSnackbar();
  const formOptions = usePatientFormOptions();

  function onSubmit(values: AppointmentTypeType) {
    const payload = {
      appointmentTypeList: {
        ...values,
        intakeForms: values.intakeForms?.map((item) => item.uuid).join(","),
      },
      xTENANTID: getXtenantId(),
    };

    showToastAndReset(
      disclosureHost.data
        ? updateAppointmentType(payload)
        : addAppointmentType(payload)
    );
  }
  const showToastAndReset = (apiCall: Promise<unknown>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        client.invalidateQueries({
          queryKey: ["provider-group-appointment-types-list"],
        });
        disclosureHost.close();
        resetForm();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const DurationList = useMemo(getDurationList, []);

  return (
    <SideSheetDrawer
      w="35vw"
      onClose={() => {
        disclosureHost.close();
        resetForm();
      }}
      open={disclosureHost.opened}
      title={dialogTitle}
      footerActions={
        <Button onClick={() => handleSubmit()} size="large" variant="contained">
          <Text>Save</Text>
        </Button>
      }
    >
      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <Input
            label="Appointment Type Name"
            textFieldProps={{
              name: "typeName",
              value: values.typeName,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Appointment Type Name",
              inputProps: { maxLength: 100 },
            }}
            required
            error={touched?.typeName && errors?.typeName}
          />
        </Grid>

        {/* !note: removing textfield for the duration and adding select as per B.A suggestion */}
        <Grid item lg={12} xs={12}>
          <CustomSelect
            required
            disableSortOptions
            label="Duration"
            name="duration"
            options={DurationList}
            enableFilterOption
            placeholder="Select Speciality"
            value={values.duration}
            onChange={(val) => setFieldValue("duration", val)}
            error={touched?.duration && errors?.duration}
          />
        </Grid>
        <Grid item lg={12} xs={12}>
          <MaskMultiSelect
            label="Intake Forms & Consent"
            value={values.intakeForms}
            placeholder="Select Intake Forms & Consent"
            options={formOptions.map((item) => ({
              uuid: item.value,
              name: item.label,
            }))}
            optionWithCheckbox
            onChange={(v?: AppointmentTypeType["intakeForms"]) =>
              setFieldValue("intakeForms", v)
            }
            error={touched?.intakeForms && errors?.intakeForms}
          />
        </Grid>

        <Grid item lg={12} xs={12}>
          <NumberInput
            label="SLA - Due Date of Submitting Intake Forms"
            textFieldProps={{
              name: "sla",
              value: values.sla,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Number Of Days",
            }}
            // required
            error={touched?.sla && errors?.sla}
          />
          <Text variant="caption" sx={{ color: "#74797B" }}>
            {SLA_HELPER_TEXT}
          </Text>
        </Grid>
        <Grid item lg={12} xs={12}>
          <FormControlLabel
            key={"autoConfirmOnSchedule"}
            label={
              <Text>
                Automatically set appointment status to 'Confirmed' when
                scheduled
              </Text>
            }
            control={
              <Checkbox
                size="small"
                checked={values.autoConfirmOnSchedule}
                onChange={(e) =>
                  setFieldValue("autoConfirmOnSchedule", e.target.checked)
                }
              />
            }
          />
        </Grid>

        {/* <Grid item xs={12}>
          <ColorPicker
            required
            label="Appointment Color"
            value={values?.color}
            onChange={(color) => setFieldValue("color", color)}
            error={touched?.color && (errors?.color as string)}
            placeholder="Select appointment color"
          />
        </Grid> */}
      </Grid>
    </SideSheetDrawer>
  );
};

export default AppointmentTypeDrawer;

const getDurationList = () =>
  Array.from({ length: 6 }, (_, index) => {
    const value = (index + 1) * 15;

    return {
      value: value.toString(),
      label: value.toString(),
    };
  });
